import { ChevronDown, Loader2, MessageSquare } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import type { ChatMessage } from "@/features/chat/services/chatService";

interface ChatMessageListProps {
	messages: ChatMessage[];
	selectedThreadId: string | null;
	threadsCount: number;
	sendIsPending: boolean;
	scrollRef: React.RefObject<HTMLDivElement>;
	threadCreatedAt?: string | null;
}

function formatConversationDate(isoDate: string): string {
	const d = new Date(isoDate);
	return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
	messages,
	selectedThreadId,
	threadsCount,
	sendIsPending,
	scrollRef,
	threadCreatedAt,
}) => {
	const [showScrollIndicator, setShowScrollIndicator] = useState(false);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const threshold = 60;

		const handleScroll = () => {
			const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > threshold;
			setShowScrollIndicator(hasMore);
		};

		handleScroll();
		el.addEventListener("scroll", handleScroll);

		return () => {
			el.removeEventListener("scroll", handleScroll);
		};
	}, [scrollRef]);

	const scrollToBottom = useCallback(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
		}
	}, [scrollRef]);

	if (!selectedThreadId) {
		return (
			<div ref={scrollRef} className="chat-message-list">
				<div className="chat-message-empty flex-1">
					<div className="text-center">
						<MessageSquare className="chat-message-empty-icon" />
						<p>
							{threadsCount === 0
								? "Start typing below to create your first thread."
								: "Select a thread, or start a new one by typing below or clicking +."}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const hasMessages = messages.length > 0;
	const displayDate = hasMessages && threadCreatedAt ? formatConversationDate(threadCreatedAt) : null;

	return (
		<div ref={scrollRef} className="chat-message-list">
			{!hasMessages ? (
				<div className="chat-message-empty flex-1">Send a message to start the conversation.</div>
			) : (
				<div className="chat-message-list-inner">
					{displayDate && <div className="chat-message-date">{displayDate}</div>}
					{messages.map((msg) =>
						msg.role === "user" ? (
							<div key={msg.id} className="w-fit max-w-full ml-auto">
								<div className="chat-message-bubble-user">
									<p className="whitespace-pre-wrap break-words">{msg.content}</p>
								</div>
							</div>
						) : (
							<div key={msg.id} className="chat-message-document">
								<ReactMarkdown>{msg.content}</ReactMarkdown>
							</div>
						),
					)}
					{sendIsPending && (
						<div className="chat-message-thinking">
							<div className="chat-message-thinking-bubble">
								<Loader2 className="h-4 w-4 shrink-0 animate-spin" />
								<span>Thinking...</span>
							</div>
						</div>
					)}
					{showScrollIndicator && (
						<div className="chat-scroll-indicator">
							<button
								type="button"
								onClick={scrollToBottom}
								className="chat-scroll-indicator-btn"
								aria-label="Scroll to bottom"
							>
								<ChevronDown className="h-5 w-5" />
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
